/**
 * Coordinates one user's Box OAuth session with shared workspace configuration.
 *
 * OAuth consent remains user-scoped and short-lived even when the resulting
 * import targets a shared Workspace Vault. The hook owns browser callback and
 * session state while backend routes own secrets and administrator authority.
 */
import { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  disconnectBox,
  exchangeBoxCode,
  getBoxOAuthConfig,
  refreshBoxSession,
} from '../services/api'
import {
  beginBoxOAuth,
  clearBoxOAuthQuery,
  clearBoxToken,
  consumeBoxOAuthCallback,
  getBoxToken,
  storeBoxToken,
} from '../services/box-session'
export const useBoxConnection = (
  api: NodeApiClient,
  orgId: string,
  vaultId: string,
  enabled = true,
) => {
  const [token, setToken] = useState(() => getBoxToken(orgId, vaultId))
  const [open, setOpen] = useState(false)
  const [feedId, setFeedId] = useState<string>()
  const [refreshing, setRefreshing] = useState(false)
  const configQuery = useQuery({
    queryKey: ['vault-box-config', orgId, vaultId],
    queryFn: () => getBoxOAuthConfig(api, orgId, vaultId),
    enabled,
    retry: false,
  })

  useEffect(() => {
    let callback
    try {
      callback = consumeBoxOAuthCallback()
    } catch (error) {
      clearBoxOAuthQuery()
      toast.error((error as Error).message)
      return
    }
    if (!callback) return
    clearBoxOAuthQuery()
    if (callback.orgId !== orgId || callback.vaultId !== vaultId) {
      toast.error('Return to the Vault where Box was connected.')
      return
    }
    void exchangeBoxCode(api, orgId, vaultId, callback.code, callback.redirectUri)
      .then((session) => {
        storeBoxToken(orgId, vaultId, session)
        setToken(session.accessToken)
        setFeedId(callback.feedId)
        setOpen(true)
        void configQuery.refetch()
        toast.success('Box connected securely')
      })
      .catch(error => toast.error(error?.response?.data?.detail ?? 'Box connection failed'))
  }, [api, orgId, vaultId])

  const connect = useCallback(() => {
    const clientId = configQuery.data?.clientId
    if (!clientId) {
      toast.error('Box OAuth app setup is required')
      return
    }
    beginBoxOAuth(clientId, orgId, vaultId, feedId)
  }, [configQuery.data?.clientId, feedId, orgId, vaultId])

  const openSource = useCallback(async (nextFeedId?: string) => {
    setFeedId(nextFeedId)
    setOpen(true)
    const active = getBoxToken(orgId, vaultId)
    if (active) {
      setToken(active)
      return
    }
    setToken(null)
    if (!configQuery.data?.connected) return
    setRefreshing(true)
    try {
      const session = await refreshBoxSession(api, orgId, vaultId)
      storeBoxToken(orgId, vaultId, session)
      setToken(session.accessToken)
    } catch {
      await configQuery.refetch()
      clearBoxToken(orgId, vaultId)
      setToken(null)
    } finally {
      setRefreshing(false)
    }
  }, [api, configQuery, orgId, vaultId])

  const changeOpen = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setFeedId(undefined)
  }, [])

  const disconnect = useCallback(async () => {
    try {
      await disconnectBox(api, orgId, vaultId)
      clearBoxToken(orgId, vaultId)
      setToken(null)
      changeOpen(false)
      await configQuery.refetch()
      toast.success('Disconnected from Box')
      return true
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? 'Box disconnect failed')
      return false
    }
  }, [api, changeOpen, configQuery, orgId, vaultId])

  const refreshConfig = useCallback(async () => {
    await configQuery.refetch()
  }, [configQuery])

  return {
    config: configQuery.data,
    // cm-coding: A durable Box grant can renew when the dialog opens. Treat
    // that renewal as loading so Configure never flashes a false disconnected
    // action that could start a second OAuth flow while refresh is in flight.
    loading: configQuery.isPending || refreshing,
    token,
    feedId,
    open,
    setOpen: changeOpen,
    connect,
    openSource,
    disconnect,
    refreshConfig,
  }
}
